import { ComponentProps, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useFiltersApi } from '../../../hooks'
import {
    formatFirstFacilityAddressField,
    formatSecondFacilityAddressField,
} from '../../../utils/formatFacilityAddress'

import { FilterProps } from '../../common/Filter/types'
import { SelectFilter } from '../../common/Filter/SelectFilter/SelectFilter'
import { type Option } from '../../common/Form/types'

import { useFilterLabels } from './useFilterLabels'
import { GetSubmetersRequestParams } from '@/hooks/useApi/useFiltersApi/types'
import { type Submeter } from '@/hooks/useApi/types'

export type SubmeterFilterProps = {
    name: string
    onChange: (subMeterIds: string[]) => void
    value: string[]
    defaultValue: string[]
    valueLabel?: string
    params: Partial<GetSubmetersRequestParams>
    className?: string
} & Pick<
    FilterProps,
    'containerRef' | 'disabled' | 'disabledLabel' | 'notAvailable'
>

export const SubmeterFilter: React.FC<SubmeterFilterProps> = (props) => {
    const { value, defaultValue, name, notAvailable, params, onChange } = props
    const { useSubmetersQuery } = useFiltersApi()

    const query = useSubmetersQuery(params)
    const { data, isLoading, isError, isFetched, isFetching } = query
    const labels = useFilterLabels()
    const { t } = useTranslation('translations')

    const optionMap = useMemo<Map<string, Submeter>>(() => {
        if (!data) return new Map<string, Submeter>()
        return new Map(
            data.map((subMeter) => [subMeter.meterId.toString(), subMeter])
        )
    }, [data])

    const options = useMemo(
        () =>
            data?.map((subMeter) => ({
                value: subMeter.meterId.toString(),
                label: subMeter.meterName,
            })),
        [data]
    )

    const renderRichLabel = useCallback(
        (
            option: Option,
            highlightMatchingText?: (
                input: string
            ) => string | React.JSX.Element
        ) => {
            if (typeof option.value !== 'string') return option.label

            const rawOption = optionMap.get(option.value)

            if (rawOption) {
                const firstAddressField = formatFirstFacilityAddressField(
                    rawOption.facilityAddress
                )
                const secondAddressField = formatSecondFacilityAddressField(
                    rawOption.facilityAddress
                )

                return (
                    <div className="space-y-0.5">
                        <div>
                            {highlightMatchingText?.(rawOption.meterName) ??
                                rawOption.meterName}
                        </div>
                        <div className="text-body-small text-subtle">
                            {highlightMatchingText?.(firstAddressField) ??
                                firstAddressField}
                        </div>
                        {secondAddressField && (
                            <div className="text-body-small text-subtle">
                                {highlightMatchingText?.(secondAddressField) ??
                                    secondAddressField}
                            </div>
                        )}
                    </div>
                )
            }

            return option.label
        },
        [optionMap]
    )

    const useSearchParams = useCallback(
        (searchValue: string) => ({
            options: options,
            compareFn: (option: Option) => {
                if (typeof option.value !== 'string') return false

                const rawOption = optionMap.get(option.value)
                if (!rawOption) {
                    return false
                }
                const firstAddressField = formatFirstFacilityAddressField(
                    rawOption.facilityAddress
                )
                const secondAddressField = formatSecondFacilityAddressField(
                    rawOption.facilityAddress
                )

                return (
                    rawOption.meterName
                        .toLowerCase()
                        .trim()
                        .includes(searchValue.toLowerCase().trim()) ||
                    rawOption.facilityName
                        .toLowerCase()
                        .trim()
                        .includes(searchValue.toLowerCase().trim()) ||
                    firstAddressField
                        .toLowerCase()
                        .trim()
                        .includes(searchValue.toLowerCase().trim()) ||
                    secondAddressField
                        .toLowerCase()
                        .trim()
                        .includes(searchValue.toLowerCase().trim())
                )
            },
        }),
        [options, optionMap]
    )

    const passedProps: ComponentProps<typeof SelectFilter> = {
        name,
        onChange: onChange,
        config: {
            value: value,
            options: options,
        },
        searchConfig: {
            name: `${name}-search`,
            placeholder: t('filters.subMeter.searchPlaceholder'),
        },
        value: value,
        defaultValue: defaultValue,
        renderRichLabel,
        label: t(`filters.subMeter.label`),
        disabled: props.disabled,
        disabledLabel: props.disabledLabel,
        notAvailable,
        useSearchParams,
        isLoading: isLoading || isFetching || !isFetched,
        isError,
        ...labels,
        valueLabel: props.valueLabel,
        containerRef: props.containerRef,
        className: props.className,
    }

    return <SelectFilter {...passedProps} />
}
